import { useCallback, useMemo, useState } from "react";
import Dialog from "../../../Common/Dialog/Dialog";
import ItemList from "../../../Common/ItemList/ItemList";
import { DialogContent, Grid, NoSsr } from "@mui/material";
import DialogTitle from "../../../Common/Dialog/DialogTitle";
import { useTranslation } from "react-i18next";
import CreditCard from "./CreditCard";
import CreditCardSkeleton from "./CreditCardSkeleton";
import { UserCreditCardStore } from "../../../UserData/_stores/UserCreditCardStore";
import AddCreditCardDialog from "./AddCreditCardDialog";
import AccountEmptyCard from "../../AccountEmptyCard";

const LIMIT = 12;

interface Props {
  opened: boolean
  onClose: () => void
  onSelect: (id: number) => void
}

export default function SelectCreditCardDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const [addDialogOpened, setAddDialogOpened] = useState(false)

  const loadingGrid = useMemo(() => {
    const listSkeletons = []

    for (let i = 0; i < LIMIT; i++) {
      listSkeletons.push(<CreditCardSkeleton key={i} />)
    }

    return listSkeletons
  }, [])

  const renderCreditCard = useCallback((item: UserCreditCardStore) => {
    return <CreditCard
      creditCard={item}
      onSelect={props.onSelect}
    />
  }, [props.onSelect])

  const onAddOpen = useCallback(() => {
    setAddDialogOpened(true)
  }, [])

  const onAddClose = useCallback(() => {
    setAddDialogOpened(false)
  }, [])

  const renderList = useCallback((items: JSX.Element | null, links: JSX.Element | null, loading: boolean) => {
    return <>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {loading
          ? loadingGrid
          : (items ?? <Grid item xs={12} md={6} lg={4}>
            <AccountEmptyCard
              title={t('No saved credit cards yet')}
              description={t('Add a card to use it for automatic store billing.')}
              actionLabel={t('Add Credit card')}
              onAction={onAddOpen}
            />
          </Grid>)
        }
      </Grid>

      {links}
    </>
  }, [loadingGrid, onAddOpen, t])

  return <Dialog
    open={props.opened}
    maxWidth="md"
    fullWidth
    onClose={props.onClose}
    showCloseButton
    ornaments="shapes-2"
  >
    <DialogTitle>{t('Select Credit card')}</DialogTitle>
    <DialogContent>
      <ItemList
        dataType="creditCards"
        controlled={true}
        item={renderCreditCard}
        limit={LIMIT}
        render={renderList}
      />
    </DialogContent>

    <NoSsr>
      <AddCreditCardDialog
        opened={addDialogOpened}
        onClose={onAddClose}
      />
    </NoSsr>
  </Dialog>
}
